import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, Square, X } from 'lucide-react';
import { useRecording } from '../hooks/useRecording';

interface RecordingDialogProps {
  onClose: () => void;
}

export function RecordingDialog({ onClose }: RecordingDialogProps) {
  const { t } = useTranslation();
  const { recording, devices, livePeaks, getDevices, selectMic, startRecording, stopRecording } = useRecording();

  useEffect(() => {
    getDevices();
  }, [getDevices]);

  // Simple VU from live peaks
  const rms = livePeaks
    ? Math.sqrt(livePeaks.reduce((sum, v) => sum + v * v, 0) / livePeaks.length)
    : 0;
  const vuLevel = Math.min(1, rms * 5); // Scale for visibility

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-[#1A1A1E] border border-[#48484A] rounded-xl p-4 w-80 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-[#E5E5EA]">
            {t('editor.record', 'Record Audio')}
          </span>
          <button onClick={onClose} className="p-1 hover:bg-[#2A2A2E] rounded">
            <X size={16} color="#8E8E93" />
          </button>
        </div>

        {/* Mic selection */}
        <div className="mb-3">
          <label className="text-[10px] text-[#8E8E93] mb-1 block">
            {t('editor.selectMic', 'Select Microphone')}
          </label>
          <select
            onChange={(e) => selectMic(e.target.value)}
            className="w-full bg-[#2A2A2E] border border-[#48484A] rounded px-2 py-1 text-xs text-[#E5E5EA]"
          >
            {devices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Microphone ${d.deviceId.slice(0, 8)}`}
              </option>
            ))}
          </select>
        </div>

        {/* VU meter */}
        {recording && (
          <div className="mb-3">
            <div className="h-3 bg-[#2A2A2E] rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-75 rounded-full"
                style={{
                  width: `${vuLevel * 100}%`,
                  background: vuLevel > 0.8 ? '#ef4444' : vuLevel > 0.6 ? '#eab308' : '#07E5B0',
                }}
              />
            </div>
            <div className="text-center text-[10px] text-red-400 mt-1 animate-pulse">
              {t('editor.recording', 'Recording...')}
            </div>
          </div>
        )}

        {/* Record / Stop button */}
        <div className="flex justify-center">
          {recording ? (
            <button
              onClick={() => {
                stopRecording();
                onClose();
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
            >
              <Square size={16} />
              {t('editor.stop', 'Stop')}
            </button>
          ) : (
            <button
              onClick={() => startRecording()}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
            >
              <Mic size={16} />
              {t('editor.record', 'Record')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
